import { apiClient } from './client'
import { ApiError } from './errors'

export interface WalletSummary {
  balance: number
  walletBalance: number
  expirableWalletCreditBalance: number
  withdrawableAmount: number
  withheldFunds: number
  rewardCredit: number
  voucherCredit: number
}

export interface ActivityEntry {
  id: number
  amount: number
  createdAt: string
  source?: string
  vault?: { id: number; name: string }
}

export interface WalletActivity {
  entries: ActivityEntry[]
  totalCount: number
  pageInfo: { hasNextPage: boolean }
}

export interface PerformanceEntry {
  spotDate: string
  totalHoldingBalance: number
}

export async function fetchWallet(): Promise<WalletSummary> {
  try {
    const response = await apiClient.get<WalletSummary>('wallet')
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

// BE /wallet/activity row shape (snake): one wallet_entry per row.
interface WalletActivityItem {
  id: number
  debit: string | null
  credit: string | null
  source_type: string
  source_id: number
  vault_id: number
  created_at: string | null
}

interface WalletActivityResponse {
  items: WalletActivityItem[]
  total: number
  page: number
  page_size: number
}

export async function fetchWalletActivity(
  page: number,
  pageSize: number
): Promise<WalletActivity> {
  try {
    // BE returns { items: [{ id, debit, credit, source_type, source_id,
    // vault_id, created_at }], total, page, page_size } (snake). Map each row to
    // the UI ActivityEntry: amount = credit - debit, source = source_type,
    // vault = { id: vault_id }.
    const response = await apiClient.get<WalletActivityResponse>(
      'wallet/activity',
      {
        params: { page, page_size: pageSize }
      }
    )
    const data = response.data
    const items = data?.items ?? []
    const entries: ActivityEntry[] = items.map(item => ({
      id: item.id,
      amount: Number(item.credit ?? 0) - Number(item.debit ?? 0),
      createdAt: item.created_at ?? '',
      source: item.source_type,
      vault: item.vault_id != null ? { id: item.vault_id, name: '' } : undefined
    }))
    const total = data?.total ?? 0
    const currentPage = data?.page ?? page
    const perPage = data?.page_size ?? pageSize
    return {
      entries,
      totalCount: total,
      pageInfo: { hasNextPage: currentPage * perPage < total }
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchWalletPerformanceHistory(
  productIds?: number[],
  vaultIds?: number[],
  timeframe?: string
): Promise<PerformanceEntry[]> {
  try {
    const response = await apiClient.get<PerformanceEntry[]>(
      'wallet/performance_history',
      {
        params: {
          ...(productIds ? { product_ids: productIds } : {}),
          ...(vaultIds ? { vault_ids: vaultIds } : {}),
          ...(timeframe ? { timeframe } : {})
        }
      }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
